import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { avatarUrl } from '@/lib/avatar'
import { interactionsApi } from '@/lib/commandsApi'
import {
  bestCommand,
  booleanCandidates,
  buildAutocompleteOptions,
  buildCommandOptions,
  checkOption,
  choiceCandidates,
  commandQuery,
  exactCandidate,
  filterCommands,
  fixedChoices,
  flattenCommands,
  pickCandidates,
  pickedLabel,
  pickKind,
  requiredOptions,
  typedCommand,
  type Candidate,
  type CommandEntry,
  type FieldState,
  type FieldValues,
  type FileLike,
  type PickEnv,
} from '@/lib/commands'
import { socialApi } from '@/lib/socialApi'
import { formatSize, uploadFile } from '@/lib/upload'
import { CommandType, OptionType, type AttachmentRef, type CommandApplication, type CommandChoice, type CommandOption } from '@/types/api'
import { indexQuery, type CommandSource } from './useCommandIndex'

/** Команда, выбранная в поле ввода: её опции и что в них набрано. */
interface ActiveCommand {
  entry: CommandEntry
  values: FieldValues
  /** Необязательные опции, добавленные в поле, — по порядку добавления. */
  added: string[]
  /** Поле под курсором: над ним — подсказка и варианты. */
  focused: string | null
  /** Ошибки после попытки отправить: показываем, пока поле не поправят. */
  errors: Record<string, string>
}

interface Autocomplete {
  /** Команда, поле и текст, на который пришли варианты. */
  key: string
  loading: boolean
  choices: CommandChoice[]
  error?: string | undefined
}

/**
 * Поле у команды без опций: в нём ловим Enter, Esc и Backspace. «#» в имени опции не бывает
 * (имена — буквы, цифры, «-» и «_»), так что с настоящей опцией не спутать.
 */
export const NO_OPTIONS = '#'
/** Куда вернуть фокус после команды: обычное поле ввода. Тоже не может быть именем опции. */
const TEXT = '#текст'

interface Options {
  source: CommandSource
  /** Можно вызывать команды: поле ввода активно и есть право USE_APPLICATION_COMMANDS. */
  enabled: boolean
  guildId: string | null
  channelId: string
  draft: string
  setDraft: (text: string) => void
  textarea: RefObject<HTMLTextAreaElement | null>
  /** Кого и что можно выбрать в опциях: участники, роли, каналы. */
  env: PickEnv
  /** Потолок размера файла опции-вложения — тот же, что у вложений сообщений. */
  maxFileSize: number
  /** Команда уходит: чат прокручивается вниз, как после своего сообщения, — там появится ответ. */
  onSend?: (() => void) | undefined
}

const without = (errors: Record<string, string>, name: string) => {
  if (!(name in errors)) return errors
  const next = { ...errors }
  delete next[name]
  return next
}

/**
 * Ошибка, которую видно сразу при наборе: число вне пределов или не число, строка длиннее
 * предела. Короткую строку и выбор из списка проверяем при отправке — пока человек печатает,
 * «Не короче 3 символов» после первой буквы только мешает. Одинокий знак «-» — начало числа.
 */
function liveError(opt: CommandOption, field: FieldState | undefined): string | undefined {
  if (!field || field.picked || fixedChoices(opt)) return undefined
  const numeric = opt.type === OptionType.Integer || opt.type === OptionType.Number
  if (numeric && /^[-+]?$/.test(field.text.trim())) return undefined
  const c = checkOption(opt, field)
  if (c.state !== 'error') return undefined
  if (numeric) return c.error
  return opt.type === OptionType.String && opt.max_length !== undefined && [...field.text.trim()].length > opt.max_length ? c.error : undefined
}

/**
 * Слэш-команды в поле ввода — общее для чата сервера и лички с ботом (ADR 0010).
 *
 * «/» в начале поля открывает список команд; выбранная команда встаёт в поле плашкой, а за ней —
 * поля опций, как у Discord: обязательные сразу, необязательные — по нажатию. Enter отправляет
 * POST /interactions (type 2) и ждёт, пока сервер скажет, ответил ли бот; поле в это время неактивно.
 */
export function useSlashCommands({ source, enabled, guildId, channelId, draft, setDraft, textarea, env, maxFileSize, onSend }: Options) {
  const [wanted, setWanted] = useState(false)
  // выделенная строка списка команд; null — лучшая под набранное (bestCommand)
  const [active, setActive] = useState<number | null>(null)
  // Esc в списке команд: прячем его до следующей правки текста
  const [dismissed, setDismissed] = useState<string | null>(null)
  const [command, setCommand] = useState<ActiveCommand | null>(null)
  const [focusIn, setFocusIn] = useState(false)
  const [pending, setPending] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [auto, setAuto] = useState<Autocomplete | null>(null)
  // выделенный вариант под полем; key — поле и текст: набрали другое — выделение по умолчанию
  const [nav, setNav] = useState<{ key: string; index: number } | null>(null)
  const listId = useId()
  const optionListId = useId()

  const available = enabled && source !== null
  const query = commandQuery(draft)
  const index = useQuery({ ...indexQuery(source), enabled: available && (wanted || query !== null), refetchOnWindowFocus: true })
  const entries = useMemo(() => flattenCommands(index.data), [index.data])
  const items = useMemo(() => (query === null ? [] : filterCommands(entries, query)), [entries, query])
  const status: 'loading' | 'error' | 'ready' = index.isLoading ? 'loading' : index.isError && !index.data ? 'error' : 'ready'
  const pickerOpen = available && !command && query !== null && dismissed !== draft && (items.length > 0 || status !== 'ready')
  const activeItem = Math.min(active ?? bestCommand(items, query ?? ''), Math.max(items.length - 1, 0))

  // --- фокус полей ------------------------------------------------------------------------
  const fields = useRef(new Map<string, HTMLElement>())
  const focusLater = useRef<string | null>(null)
  const fieldRef = useCallback(
    (name: string) => (el: HTMLElement | null) => {
      if (el) fields.current.set(name, el)
      else fields.current.delete(name)
    },
    [],
  )
  /** Поле или обычное поле ввода, если можно: есть в DOM, видно и не выключено. Курсор — в конец. */
  const tryFocus = (name: string): boolean => {
    const el = name === TEXT ? textarea.current : fields.current.get(name)
    if (!el || (el as HTMLInputElement).disabled || el.hidden) return false
    el.focus()
    if (el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && el.type === 'text')) el.setSelectionRange(el.value.length, el.value.length)
    return true
  }
  /**
   * Фокус в поле; если его ещё нет в DOM (только что добавили), оно выключено (ждём ответа)
   * или спрятано (поле ввода в режиме команды) — сразу после отрисовки, где оно появится.
   */
  const focusField = (name: string) => {
    focusLater.current = tryFocus(name) ? null : name
  }
  useLayoutEffect(() => {
    const name = focusLater.current
    if (name !== null && tryFocus(name)) focusLater.current = null
  })

  const focusText = () => focusField(TEXT)

  // --- автодополнение ---------------------------------------------------------------------
  const acTimer = useRef(0)
  const acSeq = useRef(0)
  const acKey = (cmd: ActiveCommand, name: string) => `${cmd.entry.key}\n${name}\n${cmd.values[name]?.text ?? ''}`

  const runAutocomplete = async (cmd: ActiveCommand, name: string) => {
    const seq = ++acSeq.current
    const key = acKey(cmd, name)
    setAuto((prev) => ({ key, loading: true, choices: prev?.key.startsWith(`${cmd.entry.key}\n${name}\n`) ? prev.choices : [] }))
    const res = await interactionsApi.run({
      type: 4,
      application_id: cmd.entry.command.application_id,
      channel_id: channelId,
      ...(guildId ? { guild_id: guildId } : {}),
      data: { id: cmd.entry.command.id, name: cmd.entry.command.name, type: cmd.entry.command.type ?? CommandType.ChatInput, options: buildAutocompleteOptions(cmd.entry, cmd.values, cmd.added, name) },
    })
    if (seq !== acSeq.current) return
    if (res.kind === 'choices') setAuto({ key, loading: false, choices: res.choices.slice(0, 25) })
    else if (res.kind === 'ok') setAuto({ key, loading: false, choices: [] })
    else if (res.status === 429) {
      // подсказки тоже считаются взаимодействиями: упёрлись в предел — спросим ещё раз чуть позже
      setAuto((prev) => (prev ? { ...prev, loading: false } : null))
      acTimer.current = window.setTimeout(() => void runAutocomplete(cmd, name), Math.max(1000, (res.retryAfter ?? 1) * 1000))
    } else setAuto({ key, loading: false, choices: [], error: res.message })
  }

  /** Подсказки бота на каждый ввод — с задержкой 300 мс, чтобы не спрашивать на каждую букву. */
  const requestAutocomplete = (cmd: ActiveCommand, name: string, delay: number) => {
    window.clearTimeout(acTimer.current)
    const opt = cmd.entry.options.find((o) => o.name === name)
    if (!opt?.autocomplete || cmd.values[name]?.picked) return
    acTimer.current = window.setTimeout(() => void runAutocomplete(cmd, name), delay)
  }

  const stopAutocomplete = () => {
    window.clearTimeout(acTimer.current)
    acSeq.current++
    setAuto(null)
  }

  // ушли из канала — отложенный запрос подсказок уже никому не нужен
  useEffect(() => {
    const timer = acTimer
    return () => window.clearTimeout(timer.current)
  }, [])

  // --- выбор команды ----------------------------------------------------------------------
  const choose = (entry: CommandEntry) => {
    setDraft('')
    setError(null)
    setDismissed(null)
    setNav(null)
    stopAutocomplete()
    setCommand({ entry, values: {}, added: [], focused: null, errors: {} })
    focusField(requiredOptions(entry)[0] ?? NO_OPTIONS)
  }

  /** Esc — команда уходит совсем; Backspace в пустом первом поле — обратно в текст «/имя», как у Discord. */
  const cancel = (restore: boolean) => {
    const text = restore && command ? `/${command.entry.name}` : ''
    setCommand(null)
    setError(null)
    setNav(null)
    setActive(null)
    setDismissed(null)
    stopAutocomplete()
    setDraft(text)
    focusText()
  }

  // --- поле ввода в обычном режиме --------------------------------------------------------
  const onTextFocus = () => {
    if (available) setWanted(true)
  }

  const onTextChange = (text: string) => {
    setActive(null)
    if (dismissed !== null && dismissed !== text) setDismissed(null)
    const q = commandQuery(text)
    if (q === null || !available) return
    setWanted(true)
    // имя команды целиком и пробел — сразу в режим команды
    const hit = typedCommand(entries, q)
    if (hit) choose(hit)
  }

  /** Стрелки, Enter/Tab и Esc в списке команд; true — клавиша съедена списком. */
  const onTextKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!pickerOpen) return false
    const n = items.length
    switch (e.key) {
      case 'ArrowDown':
        if (!n) return false
        e.preventDefault()
        setActive((activeItem + 1) % n)
        return true
      case 'ArrowUp':
        if (!n) return false
        e.preventDefault()
        setActive((activeItem - 1 + n) % n)
        return true
      case 'Enter':
      case 'Tab': {
        const entry = items[activeItem]
        if (e.shiftKey) return false
        if (!entry) {
          // список ещё грузится: Enter не должен отправить «/игр» обычным сообщением
          if (e.key !== 'Enter' || status !== 'loading') return false
          e.preventDefault()
          return true
        }
        e.preventDefault()
        choose(entry)
        return true
      }
      case 'Escape':
        e.preventDefault()
        setDismissed(draft)
        return true
      default:
        return false
    }
  }

  // --- поля опций -------------------------------------------------------------------------
  const options = command?.entry.options ?? []
  const shownNames = command ? [...requiredOptions(command.entry), ...command.added] : []
  const shown = shownNames.map((n) => options.find((o) => o.name === n)).filter((o): o is CommandOption => Boolean(o))
  const optional = command ? options.filter((o) => !o.required && !command.added.includes(o.name)) : []
  const order = shown.length ? shown.map((o) => o.name) : [NO_OPTIONS]
  const focusedOpt = command?.focused ? options.find((o) => o.name === command.focused) : undefined

  /** Что предложить под полем: варианты, участники, роли, каналы, «Да»/«Нет», подсказки бота. */
  const candidatesFor = (opt: CommandOption, text: string, limit: number): Candidate[] => {
    const kind = pickKind(opt)
    if (kind) return pickCandidates(env, kind, text, opt.channel_types, limit)
    if (opt.type === OptionType.Boolean) return booleanCandidates(text)
    if (fixedChoices(opt)) return choiceCandidates(opt.choices ?? [], text)
    return []
  }

  const focusedField = focusedOpt && command ? command.values[focusedOpt.name] : undefined
  const listKey = focusedOpt && command ? acKey(command, focusedOpt.name) : ''
  let suggestions: Candidate[] = []
  if (focusedOpt && command) {
    if (focusedOpt.autocomplete) {
      // бот сам отбирает варианты под набранное — показываем как есть
      suggestions = auto?.key.startsWith(`${command.entry.key}\n${focusedOpt.name}\n`) ? choiceCandidates(auto.choices, '') : []
    } else {
      // уже выбрали — показываем весь список: можно передумать
      suggestions = candidatesFor(focusedOpt, focusedField?.picked ? '' : (focusedField?.text ?? ''), 10)
    }
  }
  const navigated = nav !== null && nav.key === listKey
  // у подсказок бота своё значение тоже годится: Enter без стрелок отправляет набранное
  const suggestIndex = !suggestions.length ? -1 : navigated ? Math.min(nav.index, suggestions.length - 1) : focusedOpt?.autocomplete || focusedField?.picked ? -1 : 0
  const autoState = focusedOpt?.autocomplete && command && auto?.key.startsWith(`${command.entry.key}\n${focusedOpt.name}\n`) ? auto : null

  const fieldError = (opt: CommandOption): string | undefined => command?.errors[opt.name] ?? liveError(opt, command?.values[opt.name])

  const update = (next: ActiveCommand) => setCommand(next)

  const setText = (name: string, text: string) => {
    if (!command) return
    const next = { ...command, values: { ...command.values, [name]: { text } }, errors: without(command.errors, name) }
    update(next)
    setNav(null)
    requestAutocomplete(next, name, 300)
  }

  const pick = (name: string, c: Candidate) => {
    if (!command) return
    const label = pickedLabel(c)
    update({ ...command, values: { ...command.values, [name]: { text: label, picked: { value: c.value, label } } }, errors: without(command.errors, name) })
    setNav(null)
    window.clearTimeout(acTimer.current)
    // дальше — следующее поле, как у Discord
    const next = order[order.indexOf(name) + 1]
    if (next) focusField(next)
  }

  const setFile = (name: string, file: File | null) => {
    if (!command) return
    if (file && file.size > maxFileSize) {
      update({ ...command, errors: { ...command.errors, [name]: `Файл больше ${formatSize(maxFileSize)}` } })
      return
    }
    if (file && file.size === 0) {
      update({ ...command, errors: { ...command.errors, [name]: 'Файл пустой' } })
      return
    }
    const values = { ...command.values }
    if (file) values[name] = { text: file.name, file }
    else delete values[name]
    update({ ...command, values, errors: without(command.errors, name) })
  }

  const add = (name: string) => {
    if (!command || command.added.includes(name)) return
    update({ ...command, added: [...command.added, name] })
    focusField(name)
  }

  const onFieldFocus = (name: string) => {
    setFocusIn(true)
    if (!command || command.focused === name) return
    const next = { ...command, focused: name }
    update(next)
    setNav(null)
    // подсказки бота на пустое поле — сразу, как только в него попали
    if (!auto || auto.key !== acKey(next, name)) requestAutocomplete(next, name, 0)
  }

  /**
   * Backspace в пустом поле: необязательное — убрать и назад, первое обязательное (или пустая
   * команда) — команда снова текстом «/имя», остальные — просто назад, как у Discord.
   */
  const backspaceOut = (name: string) => {
    if (!command) return
    const i = order.indexOf(name)
    const opt = options.find((o) => o.name === name)
    if (opt && !opt.required) {
      const values = { ...command.values }
      delete values[name]
      update({ ...command, added: command.added.filter((n) => n !== name), values, errors: without(command.errors, name), focused: null })
      // полей не осталось — фокус в пустое поле команды, из него следующий Backspace вернёт текст
      focusField(order[i - 1] ?? order[i + 1] ?? NO_OPTIONS)
      return
    }
    if (i <= 0) {
      cancel(true)
      return
    }
    const prev = order[i - 1]
    if (prev) focusField(prev)
  }

  const submit = async () => {
    if (!command || pending) return
    // набрали имя и не выбрали из списка — берём его, если такое одно
    let values = command.values
    for (const opt of shown) {
      const f = values[opt.name]
      if (!f || f.picked || !f.text.trim() || (!pickKind(opt) && opt.type !== OptionType.Boolean && !fixedChoices(opt))) continue
      const hit = exactCandidate(candidatesFor(opt, f.text, 50), f.text)
      if (hit) values = { ...values, [opt.name]: { text: pickedLabel(hit), picked: { value: hit.value, label: pickedLabel(hit) } } }
    }
    const built = buildCommandOptions(command.entry, values, command.added)
    const invalid = order.find((n) => built.errors[n])
    if (invalid) {
      update({ ...command, values, errors: built.errors })
      focusField(invalid)
      return
    }
    const { entry } = command
    setPending(true)
    setError(null)
    window.clearTimeout(acTimer.current)
    onSend?.()
    try {
      // файлы опций — тем же путём, что вложения сообщений: presigned-ссылки и загрузка в хранилище
      const attachments: AttachmentRef[] = []
      if (built.files.length) {
        const files = built.files.map((n) => values[n]?.file).filter((f): f is FileLike & File => f instanceof File)
        const { attachments: slots } = await socialApi.createUploads(
          channelId,
          files.map((f, i) => ({ id: String(i), filename: f.name, file_size: f.size, content_type: f.type || 'application/octet-stream' })),
        )
        const done = files.map(() => 0)
        for (const [i, f] of files.entries()) {
          const slot = slots.find((s) => s.id === String(i))
          if (!slot) throw new Error(`Нет ссылки для «${f.name}»`)
          await uploadFile(slot.upload_url, f, (p) => {
            done[i] = p
            setProgress(done.reduce((a, b) => a + b, 0) / files.length)
          })
          attachments.push({ id: String(i), filename: f.name, uploaded_filename: slot.upload_filename })
        }
      }
      const res = await interactionsApi.run({
        type: 2,
        application_id: entry.command.application_id,
        channel_id: channelId,
        ...(guildId ? { guild_id: guildId } : {}),
        data: { id: entry.command.id, name: entry.command.name, type: entry.command.type ?? CommandType.ChatInput, options: built.options, ...(attachments.length ? { attachments } : {}) },
      })
      if (res.kind === 'error') {
        setError(res.message)
        focusField(command.focused ?? order[0] ?? NO_OPTIONS)
        return
      }
      setCommand(null)
      stopAutocomplete()
      setDraft('')
      focusText()
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Не удалось загрузить файл')
      focusField(command.focused ?? order[0] ?? NO_OPTIONS)
    } finally {
      setPending(false)
      setProgress(null)
    }
  }

  const onFieldKeyDown = (name: string, e: KeyboardEvent<HTMLElement>) => {
    if (!command) return
    const isInput = e.currentTarget instanceof HTMLInputElement
    const n = name === focusedOpt?.name ? suggestions.length : 0
    switch (e.key) {
      case 'ArrowDown':
        if (!n) return
        e.preventDefault()
        setNav({ key: listKey, index: (suggestIndex + 1) % n })
        return
      case 'ArrowUp':
        if (!n) return
        e.preventDefault()
        setNav({ key: listKey, index: suggestIndex <= 0 ? n - 1 : suggestIndex - 1 })
        return
      case 'Enter': {
        // у кнопки «Выбрать файл» Enter открывает выбор файла
        if (!isInput || e.shiftKey) return
        e.preventDefault()
        const c = suggestions[suggestIndex]
        if (n && c && suggestIndex >= 0) pick(name, c)
        else void submit()
        return
      }
      case 'Tab': {
        if (e.shiftKey) return
        const c = suggestions[suggestIndex]
        if (n && c && suggestIndex >= 0 && isInput && !command.values[name]?.picked) {
          e.preventDefault()
          pick(name, c)
          return
        }
        const next = order[order.indexOf(name) + 1]
        if (next) {
          e.preventDefault()
          focusField(next)
        }
        return
      }
      case 'Escape':
        e.preventDefault()
        cancel(false)
        return
      case 'Backspace': {
        const el = e.currentTarget as HTMLInputElement
        const empty = isInput ? el.value === '' && el.selectionStart === 0 && el.selectionEnd === 0 : !command.values[name]?.file
        if (!empty) return
        e.preventDefault()
        backspaceOut(name)
        return
      }
      default:
        return
    }
  }

  const form = command
    ? {
        entry: command.entry,
        fields: shown.map((opt) => ({ option: opt, state: command.values[opt.name] ?? { text: '' }, error: fieldError(opt) })),
        optional,
        pending,
        progress,
        focused: command.focused,
        popup:
          focusIn && command.focused !== null
            ? {
                option: focusedOpt,
                error: focusedOpt ? fieldError(focusedOpt) : undefined,
                items: suggestions,
                active: suggestIndex,
                loading: Boolean(autoState?.loading) && !suggestions.length,
                failure: autoState?.error,
                /** Список должен был быть, но ничего не подошло. */
                empty: Boolean(focusedOpt && !focusedOpt.autocomplete && !suggestions.length && (pickKind(focusedOpt) || fixedChoices(focusedOpt) || focusedOpt.type === OptionType.Boolean)),
              }
            : null,
        fieldRef,
        setText,
        pick,
        setFile,
        add,
        submit,
        cancel,
        onFieldFocus,
        onFieldKeyDown,
        setActive: (i: number) => setNav({ key: listKey, index: i }),
        onFormBlur: () => setFocusIn(false),
      }
    : null

  /** Значок приложения — аватар его бота: присланный сервером или известный чату. */
  const appAvatar = (app: CommandApplication | undefined): string | undefined => {
    if (!app) return undefined
    if (app.bot?.avatar) return avatarUrl(app.bot.id, app.bot.avatar)
    const botId = app.bot_id ?? app.bot?.id
    const bot = botId ? env.users.find((u) => u.id === botId) : undefined
    return botId ? avatarUrl(botId, bot?.avatar) : undefined
  }

  return {
    /** Список команд над полем: открыт, пока набирают «/…» и есть что показать. */
    picker: pickerOpen ? { items, active: activeItem, status, setActive, choose, listId } : null,
    form: form ? { ...form, listId: optionListId } : null,
    error,
    appAvatar,
    onTextFocus,
    onTextChange,
    onTextKeyDown,
    /** Поле ввода связано со списком команд, пока он открыт: экранный диктор читает выделенную строку. */
    textAria: pickerOpen
      ? { 'aria-controls': listId, 'aria-autocomplete': 'list' as const, 'aria-activedescendant': items.length ? `${listId}-${activeItem}` : undefined }
      : {},
  }
}

export type SlashCommands = ReturnType<typeof useSlashCommands>
export type SlashForm = NonNullable<SlashCommands['form']>
